import { Button } from "components/Button/Button";
import { IconCollection } from "components/Icon/IconCollection";
import { Popover } from "components/Popover/Popover";
import "./InlineActionButtons.scss";

export interface OverflowActionItem {
    key: string;
    label: string;
    icon: any;
    disabled?: boolean;
    onClick: () => void;
}

/**
 * Overflow reveal for a clipped `InlineActionButtons` row.
 *
 * When the inline icon row is narrower than its icons (detected by
 * `useHorizontalOverflow`), the trailing icons are clipped and only a right-edge
 * fade cue is shown. The fade alone is just a cue — the clipped icons stay
 * inaccessible, so an action "silently disappears when the column is narrow"
 * (Margot, operator-task-fe-list-view-row-height-harmonize-real). This kebab
 * opens a Popover listing EVERY inline action as a labelled row, so no action is
 * ever unreachable regardless of column width — mirroring the hidden-actions
 * Popover pattern already used by `ActionToolbar`.
 *
 * It is rendered OUTSIDE the measured `.inlineActionButtons` row, as an in-flow
 * flex sibling in its own reserved right-hand gutter (so it never paints on top of
 * the action icons, and the icon-row fade sits to its left). Being a sibling,
 * mounting it only shrinks the measured icon row's width further — monotonically
 * more clipped, never flipping `isOverflowing` back off — so it does not create a
 * measure↔render feedback loop with `useHorizontalOverflow`.
 */
export default function InlineActionsOverflow({ items }: { items: OverflowActionItem[] }) {
    if (items.length === 0) {
        return null;
    }
    return (
        <div className="inlineActionsOverflow">
            <Popover
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
                contentClassName="inlineActionsOverflowMenu"
                trigger={
                    <Button
                        icon={IconCollection.dotsVertical}
                        variation="clear"
                        size="small"
                        label=""
                        ariaLabel="More actions"
                    />
                }
            >
                <ul className="popoverMenu">
                    {items.map((item) => (
                        <li key={`overflow${item.key}`}>
                            <Button
                                variation="clear"
                                icon={item.icon}
                                label={item.label}
                                disabled={item.disabled}
                                onClick={item.onClick}
                            />
                        </li>
                    ))}
                </ul>
            </Popover>
        </div>
    );
}
